import { getSetting } from '../../../../utils/settings';
import { sizeOf } from './nodeSize';
import { place, settleBoardWires, warmBoardPicture } from './placeCamera';

const BUSY_TARGET = [
  '[data-board-node]',
  '.task-node',
  '.decision-node',
  '.note-node',
  '.milestone-node',
  '.handles-layer__handle',
  '.handles-layer__connection-handle',
  '.handles-layer__branch-btn',
  '.handles-layer__drag-handle',
  '.handles-layer__edge-insert',
  '.chain-origin',
  '.canvas-context-menu',
  '.connection-menu',
  '.edge-insert-menu',
  '.flowchart-minimap',
  '.flowchart-hud',
  'button',
  'input',
  'textarea',
  'select',
  'a',
  '[role="button"]',
].join(',');

/** Empty board space, not a card, handle, or menu. */
export function isBoardCanvasContextTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return !target.closest(BUSY_TARGET);
}

function notifyBoardGesture() {
  window.dispatchEvent(new CustomEvent('flowchartpanstart'));
}

/** Left-drag pans. Right-drag or Alt-drag draws a selection box. */
export function attachBoardPointer({
  wrap,
  worldRef,
  boxRef,
  cameraRef,
  flyFrameRef,
  gestureRef,
  panningRef,
  boxDragRef,
  tasksRef,
  positionsRef,
  boxesRef,
  selectedRef,
  onSelectionChangeRef,
  onEmptyContextMenuRef,
}) {
  let bounds = wrap.getBoundingClientRect();
  const readBounds = () => {
    bounds = wrap.getBoundingClientRect();
  };
  const localPoint = (event) => ({
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  });

  const toWorld = (event) => {
    const point = localPoint(event);
    const camera = cameraRef.current;
    const zoom = camera.zoom || 1;
    return {
      x: (point.x - camera.x) / zoom,
      y: (point.y - camera.y) / zoom,
    };
  };

  const paintBox = (x, y, width, height) => {
    const el = boxRef.current;
    if (!el) return;
    el.style.display = 'block';
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
  };

  const hideBox = () => {
    const el = boxRef.current;
    if (el) el.style.display = 'none';
  };

  const choose = (ids) => {
    selectedRef.current = ids;
    onSelectionChangeRef.current?.(ids);
  };

  const onDown = (event) => {
    readBounds();
    const drawingBox = event.button === 2 || (event.button === 0 && event.altKey);
    if (event.button !== 0 && !drawingBox) return;
    // A button press has to paint its own menu first. Rebuilding the board
    // picture on that same press is what made the menu wait.
    if (event.target.closest('button, input, textarea, select, a, [role="button"], .flowchart-minimap, .flowchart-hud, .handles-layer, .chain-origin')) return;
    if (flyFrameRef.current) {
      cancelAnimationFrame(flyFrameRef.current);
      flyFrameRef.current = 0;
    }
    if (gestureRef) gestureRef.current = true;
    if (drawingBox) {
      event.preventDefault();
      const world = toWorld(event);
      boxDragRef.current = {
        x: event.clientX,
        y: event.clientY,
        worldX: world.x,
        worldY: world.y,
        moved: false,
        shift: event.shiftKey,
      };
      if (event.button === 2 && isBoardCanvasContextTarget(event.target)) {
        onEmptyContextMenuRef?.current?.reveal(
          { x: event.clientX, y: event.clientY },
          world,
        );
      }
      paintBox(world.x, world.y, 0, 0);
      return;
    }
    panningRef.current = {
      x: event.clientX,
      y: event.clientY,
      originX: event.clientX,
      originY: event.clientY,
      moved: false,
      camera: { ...cameraRef.current },
    };
  };

  let skipCanvasMenu = false;

  const onMove = (event) => {
    const box = boxDragRef.current;
    if (box) {
      const world = toWorld(event);
      if (!box.moved && (Math.abs(event.clientX - box.x) > 5 || Math.abs(event.clientY - box.y) > 5)) {
        box.moved = true;
        onEmptyContextMenuRef?.current?.dismiss();
        notifyBoardGesture();
      }
      const x = Math.min(box.worldX, world.x);
      const y = Math.min(box.worldY, world.y);
      paintBox(x, y, Math.abs(world.x - box.worldX), Math.abs(world.y - box.worldY));
      return;
    }
    const pan = panningRef.current;
    if (!pan) return;
    const dx = event.clientX - pan.x;
    const dy = event.clientY - pan.y;
    if (!dx && !dy) return;
    cameraRef.current = {
      ...cameraRef.current,
      x: cameraRef.current.x + dx,
      y: cameraRef.current.y + dy,
    };
    pan.x = event.clientX;
    pan.y = event.clientY;
    if (!pan.moved && (Math.abs(event.clientX - pan.originX) > 2 || Math.abs(event.clientY - pan.originY) > 2)) {
      pan.moved = true;
      notifyBoardGesture();
      // A click that only selects never rebuilds the picture. A drag does,
      // on the first real movement, so the slide that follows is smooth.
      warmBoardPicture(worldRef.current);
      wrap.classList.add('board-view--panning');
    }
    place(worldRef.current, cameraRef.current);
  };

  const onUp = (event) => {
    if (gestureRef) gestureRef.current = false;
    const box = boxDragRef.current;
    if (box) {
      boxDragRef.current = null;
      hideBox();
      if (box.moved) skipCanvasMenu = true;
      if (!box.moved) return;
      const world = toWorld(event);
      const left = Math.min(box.worldX, world.x);
      const top = Math.min(box.worldY, world.y);
      const right = Math.max(box.worldX, world.x);
      const bottom = Math.max(box.worldY, world.y);
      const picked = new Set();
      tasksRef.current.forEach((task) => {
        const pos = positionsRef.current?.get(task.id);
        if (!pos) return;
        const size = sizeOf(task, boxesRef.current);
        if (pos.x < right && pos.x + size.width > left && pos.y < bottom && pos.y + size.height > top) {
          picked.add(task.id);
        }
      });
      if (box.shift) {
        const next = new Set(selectedRef.current);
        picked.forEach((id) => next.add(id));
        choose(next);
      } else {
        choose(picked);
      }
      return;
    }
    const pan = panningRef.current;
    panningRef.current = null;
    wrap.classList.remove('board-view--panning');
    if (!pan) return;
    if (!pan.moved) {
      cameraRef.current = pan.camera;
    } else {
      return;
    }
    const card = event.target.closest('[data-board-node]');
    const id = card ? card.getAttribute('data-board-node') : null;
    if (event.shiftKey && id) {
      const next = new Set(selectedRef.current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      choose(next);
      return;
    }
    choose(id ? new Set([id]) : new Set());
  };

  const onContext = (event) => {
    if (wrap.contains(event.target)) event.preventDefault();
    if (skipCanvasMenu || boxDragRef.current?.moved) {
      skipCanvasMenu = false;
      return;
    }
    if (!isBoardCanvasContextTarget(event.target)) return;
    onEmptyContextMenuRef?.current?.reveal(
      { x: event.clientX, y: event.clientY },
      toWorld(event),
    );
  };

  let wheelFrame = 0;
  let wheelSettle = 0;
  let zoomSensitivity = 1;
  const onWheel = (event) => {
    event.preventDefault();
    if (!wheelFrame) {
      notifyBoardGesture();
      readBounds();
      if (!wheelSettle) zoomSensitivity = getSetting('trackpadZoomSensitivity') || 1;
    }
    const point = localPoint(event);
    const camera = cameraRef.current;
    let deltaY = event.deltaY;
    if (event.deltaMode === 1) deltaY *= 20;
    else if (event.deltaMode === 2) deltaY *= 100;
    const nextZoom = Math.max(0.15, Math.min(2.5, camera.zoom * Math.exp(-deltaY * 0.002 * zoomSensitivity)));
    const worldX = (point.x - camera.x) / camera.zoom;
    const worldY = (point.y - camera.y) / camera.zoom;
    cameraRef.current = {
      zoom: nextZoom,
      x: point.x - worldX * nextZoom,
      y: point.y - worldY * nextZoom,
    };
    // A trackpad sends a burst of wheel events. Move the camera once per frame,
    // and redraw the lines at the new size only after the wheel goes quiet.
    if (!wheelFrame) {
      wheelFrame = requestAnimationFrame(() => {
        wheelFrame = 0;
        place(worldRef.current, cameraRef.current);
      });
    }
    if (wheelSettle) clearTimeout(wheelSettle);
    wheelSettle = setTimeout(() => {
      wheelSettle = 0;
      settleBoardWires(worldRef.current, cameraRef.current);
    }, 80);
  };

  wrap.addEventListener('mousedown', onDown);
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
  window.addEventListener('resize', readBounds);
  wrap.addEventListener('contextmenu', onContext);
  wrap.addEventListener('wheel', onWheel, { passive: false });
  return () => {
    wrap.removeEventListener('mousedown', onDown);
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    window.removeEventListener('resize', readBounds);
    wrap.removeEventListener('contextmenu', onContext);
    wrap.removeEventListener('wheel', onWheel);
    if (wheelFrame) cancelAnimationFrame(wheelFrame);
    if (wheelSettle) clearTimeout(wheelSettle);
  };
}
